/**
 * Coach API Client — Arena Client (WP-595 / EC-630 / D-24404)
 *
 * Typed `fetch` wrappers for the endgame AI coach read endpoints:
 *   - `GET /api/me/scores/:replayHash/coach` — the Legendary-Pass endgame
 *     coaching for a scored match the caller owns (WP-594). Lazy + cached
 *     server-side; the client calls it on demand when a Pass holder opens the
 *     coach.
 *   - `GET /api/me/matches/:matchId/coach` — the same coaching for a casual
 *     (unscored) match, addressed by match id (WP-751 / WP-752 / D-24576).
 *
 * Layer-boundary contract: imports nothing from `apps/server/`,
 * `@legendary-arena/*`, `boardgame.io`, or `vue-sfc-loader`. The wire shapes are
 * declared inline by structural compatibility with the server's `CoachReport` /
 * `StoredCoachReport` (`apps/server/src/coach/coach.types.ts`) — the server is
 * authoritative; the client must not import server-layer types. Mirrors the
 * WP-339 `competitionApi.ts` never-throw Bearer-fetch pattern.
 *
 * Authority: WP-595 §Scope; EC-630; D-24404; WP-594 (the server surface).
 */

import { buildApiUrl } from './apiBaseUrl';

/**
 * The coaching report, a structural mirror of the server's `CoachReport`. The
 * client renders it verbatim.
 */
export interface CoachReport {
  readonly headline: string;
  readonly heroFit: string;
  readonly purchases: string;
  readonly suggestions: readonly string[];
  // why: WP-713/D-24536 — the deterministic play-order "opportunity" tips the server
  // computes (WP-710/D-24533) and merges onto the report before persistence. A
  // structural mirror of the server field (the layer-boundary rule above forbids
  // importing the server type); optional because reports persisted before WP-710 omit
  // it. The client renders these verbatim — it never composes or re-evaluates them.
  readonly sequenceTips?: readonly string[];
  // why: WP-718/D-24540 — the deterministic co-op "Table Cooperation" recognition the
  // server computes (WP-717) and merges onto the report before persistence. A structural
  // mirror of the server field (no server-type import); optional because reports persisted
  // before WP-717 omit it. Rendered verbatim — the client never composes it.
  readonly tableCooperation?: readonly string[];
}

/**
 * A stored coach report (mirror of the server's `StoredCoachReport`): the report
 * plus which model produced it and when.
 */
export interface StoredCoachReport {
  readonly report: CoachReport;
  readonly model: string;
  readonly generatedAt: string;
}

/**
 * Result of a coach fetch. Never throws: a network failure surfaces as
 * `status: 0`. `report`/`wasCached` are present only on HTTP 200; `error` carries
 * the server's `{ error: <code> }` string on a non-200 (e.g. `not_entitled`,
 * `coach_unavailable`).
 */
export interface FetchCoachResult {
  readonly status: number;
  readonly report: StoredCoachReport | null;
  readonly wasCached: boolean | null;
  readonly error: string | null;
}

/**
 * Best-effort extraction of the server's `{ error: <code> }` string from a
 * non-200 body. Returns `null` when absent / not JSON / no string `error`.
 *
 * @param response The non-200 fetch response.
 * @returns The error code string, or `null`.
 */
async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : null;
  } catch {
    // why: a non-200 with no/malformed body is a transport-level failure; surface
    // only the status so the caller can render a generic state.
    return null;
  }
}

/**
 * GET one coach endpoint and map the response to a `FetchCoachResult`. Shared by
 * the replay-hash and matchId fetchers so both parse and report errors
 * identically. Never throws (`status: 0` on network failure).
 *
 * @param authToken The bearer token, or `null` for an unauthenticated caller.
 * @param path The already-encoded API path.
 * @returns The fetch result (never throws).
 */
async function fetchCoachAtPath(
  authToken: string | null,
  path: string,
): Promise<FetchCoachResult> {
  let response: Response;
  try {
    response = await fetch(buildApiUrl(path), {
      method: 'GET',
      headers:
        authToken === null ? {} : { Authorization: `Bearer ${authToken}` },
    });
  } catch {
    return { status: 0, report: null, wasCached: null, error: null };
  }
  if (response.status === 200) {
    const body = (await response.json()) as {
      report: StoredCoachReport;
      wasCached: boolean;
    };
    return {
      status: 200,
      report: body.report,
      wasCached: body.wasCached,
      error: null,
    };
  }
  return {
    status: response.status,
    report: null,
    wasCached: null,
    error: await readErrorCode(response),
  };
}

/**
 * Fetch the endgame coaching for a scored match by its `replayHash`. The server
 * enforces the Legendary-Pass gate + ownership; the client calls this only for a
 * Pass holder who opened the coach. Never throws (`status: 0` on network failure).
 *
 * @param authToken The bearer token, or `null` for an unauthenticated caller.
 * @param replayHash The scored match's replay hash.
 * @returns The fetch result (never throws).
 */
export async function fetchCoachReport(
  authToken: string | null,
  replayHash: string,
): Promise<FetchCoachResult> {
  // why: encode the path segment — a replay hash is hex today, but encoding
  // keeps the URL well-formed if the id format ever widens.
  return fetchCoachAtPath(authToken, `/api/me/scores/${encodeURIComponent(replayHash)}/coach`);
}

/**
 * Fetch the endgame coaching for a casual (unscored) match by its boardgame.io
 * `matchId` (WP-752 / D-24576). The client never receives a replay hash for an
 * unscored match, so the server resolves the id to the replay itself. Same
 * parsing and errors as `fetchCoachReport`. Never throws.
 *
 * @param authToken The bearer token, or `null` for an unauthenticated caller.
 * @param matchId The finished match's boardgame.io id.
 * @returns The fetch result (never throws).
 */
export async function fetchCoachReportForMatch(
  authToken: string | null,
  matchId: string,
): Promise<FetchCoachResult> {
  // why: encode the path segment — a match id is URL-safe today, but encoding
  // keeps the URL well-formed if the id format ever widens.
  return fetchCoachAtPath(authToken, `/api/me/matches/${encodeURIComponent(matchId)}/coach`);
}
